import { TourDetailViewModel, TourType, TourViewModel } from '@api/index';
import {
  convertDateToMomentObject,
  formatISOToDate,
  formatMomentDate,
  getNDaysLater,
  GTM_DATE_FORMAT,
} from '@utils/dateUtil';
import { BookerSearchViewModel } from '@components/views/landing/components/booker/common/BookerSearchDrawer';

const getPriceRange = (tourDetail: TourDetailViewModel): number[] => {
  const prices: number[] = [];
  if (
    tourDetail &&
    Array.isArray(tourDetail?.packages) &&
    tourDetail?.packages?.length
  ) {
    tourDetail?.packages?.forEach(
      ({ price1, price2, price3, price4, price5 }) => {
        const price = price1 || price2 || price3 || price4 || price5 || 0;
        prices.push(price);
      }
    );
  }

  if (prices.length) {
    return [Math.min(...prices), Math.max(...prices)];
  }
  return [];
};

export const addTourDetailGTM = (tourDetail: TourDetailViewModel) => {
  const startDate = tourDetail.earliestDepartureDate
    ? formatISOToDate(tourDetail.earliestDepartureDate, GTM_DATE_FORMAT)
    : '';
  const endDate = tourDetail.earliestDepartureDate
    ? formatMomentDate(
        getNDaysLater(
          convertDateToMomentObject(
            tourDetail.earliestDepartureDate,
            GTM_DATE_FORMAT
          ),
          tourDetail.dayCount
        ),
        GTM_DATE_FORMAT
      )
    : '';
  const [minPrice, maxPrice] = getPriceRange(tourDetail);
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product View',
    Label: 'Product View',
    noninteraction: true,
    ecommerce: {
      detail: {
        products: [
          {
            name: tourDetail?.name, // Name or ID is required.
            id: tourDetail?.id, // Product ID
            price: tourDetail?.lowestPrice?.price, // Product Price
            brand: tourDetail?.type === 'Abroad' ? 'Yurtiçi' : 'Yurtdışı', // Product Brand
            category: tourDetail?.categoryName, // Product Category
            variant: tourDetail?.regionName, // Product Variant
            dimension59: 'Tur', // Product Service Type {//TODO: {ilker.yildirim} TOUR | CRUISE}
            dimension61: tourDetail?.placesToGo, // Product Theme
            dimension62: tourDetail?.lowestPrice?.discountRate?.toString(), // Product Discount Rate
            dimension63: Array.isArray(tourDetail?.images)
              ? tourDetail?.images?.length?.toString()
              : '0', // Product Photo Count
            dimension65: minPrice && maxPrice && minPrice + ' - ' + maxPrice, // Product Price Interval
            dimension66: tourDetail?.lowestPrice?.price, // Product Real Price
            dimension67: 'YES', // Product Installment Status
            dimension103: tourDetail?.dayCount?.toString(), // Tour Duration (day)
            dimension104: startDate, // Tour Start Date
            dimension105: endDate, // Tour End Date
            metric1: tourDetail?.lowestPrice?.discountRate, // Product Discount (Numerically)
          },
        ],
      },
    },
    event: 'eeEvent',
  });
};

export const addTourSearchBarItemClickGTM = (item: BookerSearchViewModel) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Impressions',
    Label: 'Product Impressions - Home Page',
    noninteraction: true,
    ecommerce: {
      currencyCode: 'TRY',
      impressions: [
        {
          name: item.text, // Name or ID is required.
          id: item.id, // Product ID
          list: 'Home Page - Search Bar List',
          position: 1,
          dimension59: 'Tour',
        },
      ],
    },
    event: 'eeEvent',
  });
};

export const addTourListItemToGTM = ({
  tour,
  variant,
  position,
  currency,
  isSearchedFromSearchBar,
}: {
  tour: TourViewModel;
  variant: string;
  position: number;
  currency: string;
  isSearchedFromSearchBar: boolean;
}) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Impressions',
    Label: isSearchedFromSearchBar
      ? 'Product Impressions - Search Page'
      : 'Product Impressions - Listing Page',
    noninteraction: true,
    ecommerce: {
      currencyCode: currency,
      impressions: [
        {
          name: tour?.name, // Name or ID is required.
          id: tour?.id, // Product ID,
          price: tour?.pricing?.discounted, // Product Price,
          brand: tour?.tourType === TourType.ABROAD ? 'Yurtdışı' : 'Yurtiçi', // Product Brand,
          category: tour?.categoryName, // Product Category
          variant, // Product Variant - should be region instead of subCaategoryName?
          list: isSearchedFromSearchBar
            ? 'Search Page - ' + variant
            : 'Listing Page - ' + variant, // Product List Type
          position, // Product Position
          dimension59: 'Tur', // Product Service Type -
          dimension60: 'Erken Rezervasyon', // Product Campaign -
          dimension61: tour?.placesToGo, // Product Theme
          dimension62: tour?.pricing?.rate + '%', // Product Discount Rate
          dimension63: '', // Product Photo Count -
          dimension64: '', // Product Comment Count -
          dimension65: tour?.pricing?.discounted, // Product Prive Interval -
          dimension66: tour?.pricing?.selling, // Product Real Price
          dimension67: 'YES', // Product Installment Status -
          dimension68: 'YES', // Product Payment Campaign Status -
          dimension69: 'Avanslı Ödeme', // Product Payment Campaign Details -
          dimension103: tour?.dayStay?.toString(), // Tour Duratioc (day)
          dimension104: tour?.dates?.[0]?.departureDate
            ? formatISOToDate(tour?.dates[0].departureDate, GTM_DATE_FORMAT)
            : null, // Tour Start Date -
          dimension105: tour?.dates?.[tour?.dates.length - 1]?.arrivalDate
            ? formatISOToDate(
                tour?.dates[tour?.dates.length - 1].arrivalDate,
                GTM_DATE_FORMAT
              )
            : null, // Tour End Date -
          metric1: tour?.pricing?.rate, // Product Discount (Numerically)
        },
      ],
    },
    event: 'eeEvent',
  });
};
